import type { EtlStatusResponse, StatusDisplayItem } from './etlSchemas';

/**
 * Extract a status display item from an ETL status response.
 */
function extractStatusDisplayItem(
  status: EtlStatusResponse,
): StatusDisplayItem {
  const verbatim = status.verbatim_message;
  const update = verbatim?.update;
  const url = verbatim?.url;

  return {
    type: update?.status?.type ?? undefined,
    display: update?.status?.display ?? undefined,
    timestamp: update?.status?.statusTimeUTC || status.created_at,
    vehicle: update?.vehicle ?? undefined,
    provider: update?.provider ?? undefined,
    dispatchCenter: update?.dispatchCenter ?? undefined,
    trackingUrl: url?.transportTracking ?? undefined,
    eta: update?.eta ?? undefined,
  };
}

/**
 * Comparable utc ms helper function.
 */
function statusDisplayTimeMs(
  item: Pick<StatusDisplayItem, 'timestamp'>,
): number {
  if (!item.timestamp) {
    return 0;
  }

  const ms = new Date(item.timestamp).getTime();

  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Sort statuses by timestamp in descending order (newest first).
 */
function sortStatusesByTimestamp(
  statuses: StatusDisplayItem[],
): StatusDisplayItem[] {
  return [...statuses].sort(
    (a, b) => statusDisplayTimeMs(b) - statusDisplayTimeMs(a),
  );
}

/**
 * Collapse rows that would render identically, newest-first list preserved.
 * Key uses type, display, and timestamp so real repeats at different times stay.
 */
function getStatusDisplayFingerprint(item: StatusDisplayItem): string {
  return JSON.stringify([
    item.type ?? null,
    item.display ?? null,
    item.timestamp ?? null,
  ]);
}

function dedupeStatusDisplayItemsByFingerprint(
  items: StatusDisplayItem[],
): StatusDisplayItem[] {
  const seen = new Set<string>();
  const result: StatusDisplayItem[] = [];

  for (const item of items) {
    const key = getStatusDisplayFingerprint(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(item);
  }
  return result;
}

/**
 * Format raw statuses from a transport into sorted StatusDisplayItems.
 */
function formatTransportStatuses(
  statuses: EtlStatusResponse[] | undefined,
): StatusDisplayItem[] {
  if (!statuses) {
    return [];
  }
  const items = statuses.map(extractStatusDisplayItem);
  const sorted = sortStatusesByTimestamp(items);
  return dedupeStatusDisplayItemsByFingerprint(sorted);
}

export {
  extractStatusDisplayItem,
  statusDisplayTimeMs,
  sortStatusesByTimestamp,
  formatTransportStatuses,
};
